/**
 * `.defaultScrollAnchor(_:)` on a `ScrollView` (docs/ops-protocol.md, "Default
 * scroll anchor"): the scroll view carries `defaultScrollAnchor`, a unit
 * point. The content starts there (`.bottom`: the end is in view, as a chat
 * opens on its latest message), and when the content or the viewport changes
 * size in a pass, the anchor is kept *if the viewer was at it* before the
 * pass: new lines at the end of a chat keep the end in view, while a viewer
 * who scrolled up to read stays where they are. The offset before the pass
 * is the one scrollSync.ts read for it, so a pass's own writes do not count
 * as the viewer's scrolling.
 */

import type { UnitPoint } from './protocol';
import { scrollTopOf } from './scrollSync';

/** The scroll offset on one axis that puts the anchor fraction of the content at the same fraction of the viewport. */
export function anchoredOffset(content: number, viewport: number, anchor: number): number {
  return Math.max(0, Math.round((content - viewport) * anchor));
}

/** Within a couple of pixels of the anchor: browsers round offsets, and a smooth scroll may stop short. */
export const ANCHOR_TOLERANCE = 2;

export function isAtAnchor(offset: number, content: number, viewport: number, anchor: number): boolean {
  return Math.abs(offset - anchoredOffset(content, viewport, anchor)) <= ANCHOR_TOLERANCE;
}

/** What `keepAnchor` reads and writes on a scrolling element; `HTMLElement` fits. */
export interface AnchoredScroller {
  scrollTop: number;
  scrollLeft: number;
  scrollHeight: number;
  scrollWidth: number;
  clientHeight: number;
  clientWidth: number;
}

/** The extents a scroller had after the last pass. */
interface Extents {
  content: { width: number; height: number };
  viewport: { width: number; height: number };
}

/**
 * After a pass laid the scroller out: on its first pass the content goes to
 * the anchor; after that, an axis whose extents changed keeps the anchor
 * when `offsetBefore` (the viewer's position before the pass) was at it.
 * Returns the extents to remember for the next pass.
 */
export function keepAnchor(
  el: AnchoredScroller,
  anchor: UnitPoint,
  axes: { horizontal: boolean; vertical: boolean },
  before: Extents | undefined,
  offsetBefore: { top: number; left: number },
): Extents {
  const now: Extents = { content: { width: el.scrollWidth, height: el.scrollHeight }, viewport: { width: el.clientWidth, height: el.clientHeight } };
  if (axes.vertical) {
    const changed = !before || before.content.height !== now.content.height || before.viewport.height !== now.viewport.height;
    const wasAt = !before || isAtAnchor(offsetBefore.top, before.content.height, before.viewport.height, anchor.y);
    if (changed && wasAt) el.scrollTop = anchoredOffset(now.content.height, now.viewport.height, anchor.y);
  }
  if (axes.horizontal) {
    const changed = !before || before.content.width !== now.content.width || before.viewport.width !== now.viewport.width;
    const wasAt = !before || isAtAnchor(offsetBefore.left, before.content.width, before.viewport.width, anchor.x);
    if (changed && wasAt) el.scrollLeft = anchoredOffset(now.content.width, now.viewport.width, anchor.x);
  }
  return now;
}

const anchors = new WeakMap<HTMLElement, UnitPoint>();
const extents = new WeakMap<HTMLElement, Extents>();
const anchored = new Set<HTMLElement>();

/** A scroll view's `defaultScrollAnchor` prop, kept for the passes to come (none: the view scrolls as any other). */
export function applyDefaultScrollAnchor(el: HTMLElement, anchor: UnitPoint | undefined): void {
  if (anchor && typeof anchor.x === 'number' && typeof anchor.y === 'number') {
    anchors.set(el, { x: anchor.x, y: anchor.y });
    anchored.add(el);
  } else {
    anchors.delete(el);
    extents.delete(el);
    anchored.delete(el);
  }
}

/** The axes a `scrollview` element scrolls on, from its `data-sb-axes`. */
function axesOf(el: HTMLElement): { horizontal: boolean; vertical: boolean } {
  const axes = el.dataset['sbAxes'] ?? 'vertical';
  return { horizontal: axes === 'horizontal' || axes === 'both', vertical: axes !== 'horizontal' };
}

/** Called by the renderer after each pass's frames are applied: every anchored scroll view keeps (or takes) its anchor. */
export function keepScrollAnchors(): void {
  for (const el of anchored) {
    if (!el.isConnected) {
      anchored.delete(el);
      extents.delete(el);
      continue;
    }
    const anchor = anchors.get(el);
    if (!anchor) continue;
    extents.set(el, keepAnchor(el, anchor, axesOf(el), extents.get(el), { top: scrollTopOf(el), left: el.scrollLeft }));
  }
}

/** For tests: forget every anchored scroller. */
export function resetScrollAnchors(): void {
  anchored.clear();
}
